import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import { useUiSettings } from '../../contexts/UiSettingsContext';
import Navbar from './Navbar';
import Sidenav from './Sidenav';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  console.log('✅ CORRECT Layout/Layout.tsx rendering with Navbar + Sidenav');
  const { settings } = useUiSettings();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar />
      <Box sx={{ display: 'flex', flexGrow: 1, pt: { xs: '118px', sm: '118px' } }}>
        <Sidenav />
        <Box
          component="main"
          id="main-content"
          tabIndex={-1} // Makes it focusable but not in the tab order
          aria-label="Main content"
          sx={{
            flexGrow: 1,
            width: '100%',
            minHeight: 'calc(100vh - 118px)',
            backgroundColor: 'background.default',
            px: { xs: 2, sm: 3 },
            '&:focus': {
              outline: 'none',
            },
            scrollBehavior: 'smooth',
            scrollMarginTop: '118px',
          }}
        >
          {/* Skip link target */}
          <div id="skip-target" style={{ position: 'absolute', top: 0 }}></div>
          {children}
        </Box>
      </Box>
      <Box
        component="footer"
        aria-label="Application footer"
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          px: { xs: 2, sm: 3 },
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 'auto',
        }}
      >
        {settings.enhancedFooter ? (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
              >
                © {new Date().getFullYear()} E-Unify · Data Intelligence Platform for Government
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.75rem', sm: '0.8rem' },
                  mt: 0.25,
                }}
              >
                Prototype environment – Not an official U.S. Government system.
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <Link
                href="#main-content"
                underline="hover"
                aria-label="Return to top of page"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
              >
                Return to top
              </Link>
              <Link
                href="/LICENSE.txt"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                aria-label="View the MIT License for E-Unify (opens in a new tab)"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
              >
                MIT License
              </Link>
              <Link
                href="/docs?doc=508-COMPLIANCE-COMPLETE.md"
                underline="hover"
                aria-label="View E-Unify accessibility and Section 508 statement"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
              >
                Accessibility
              </Link>
              <Link
                href="/docs?doc=PRIVACY-AND-DATA-PROTECTION.md"
                underline="hover"
                aria-label="View E-Unify privacy and data protection information"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
              >
                Privacy
              </Link>
              <Link
                href="/docs?doc=TERMS-OF-USE.md"
                underline="hover"
                aria-label="View E-Unify terms of use and legal disclaimers"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
              >
                Terms of Use
              </Link>
              <Link
                href="/contact"
                underline="hover"
                aria-label="Contact the E-Unify team"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
              >
                Contact
              </Link>
            </Box>
          </>
        ) : (
          <>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            >
              © {new Date().getFullYear()} E-Unify
            </Typography>
            <Link
              href="/LICENSE.txt"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              aria-label="View the MIT License for E-Unify (opens in a new tab)"
              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            >
              MIT License
            </Link>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Layout;
